import {safeLocalStorage} from "@/lib/utils/storage";
import {APK_DOWNLOAD_URLS} from "@/lib/utils/contants/apkDownloadUrls";

/**
 * AC-902: the Android app is downloaded through apk-service (AC-900), which bakes
 * the partner mark (`click` + `traffic`) into the APK. The app reads it back and
 * sends it on registration, so a player who installs from a partner link and
 * registers in the app is not lost to "organic".
 *
 * The URL comes from the per-environment default; `NEXT_PUBLIC_APK_DOWNLOAD_URL`
 * only overrides it. An environment with neither (unknown `NEXT_PUBLIC_APP_ENV`)
 * keeps every APK entry point switched off.
 */
const APK_DOWNLOAD_URL = process.env.NEXT_PUBLIC_APK_DOWNLOAD_URL
    || APK_DOWNLOAD_URLS[process.env.NEXT_PUBLIC_APP_ENV ?? ""];

export function isApkDownloadEnabled(): boolean {
    return !!APK_DOWNLOAD_URL;
}

/**
 * apk-service endpoint without any mark — identical on server and client, so it
 * is safe as a rendered `href` (the no-JS fallback). The partner mark and login
 * code are only added at click time by {@link startApkDownload}. Empty when the
 * environment has no apk-service.
 */
export function getApkDownloadBaseUrl(): string {
    return APK_DOWNLOAD_URL ?? "";
}

/**
 * The APK only installs on Android. `userAgentData` (Chromium) is authoritative
 * when present; the UA string covers everything else.
 */
export function isAndroidDevice(): boolean {
    if (typeof navigator === "undefined") return false;
    const platform = (navigator as {userAgentData?: {platform?: string}}).userAgentData?.platform;
    if (platform) return platform === "Android";
    return /Android/i.test(navigator.userAgent);
}

/**
 * Download URL with the same `ref`/`traffic` the registration payloads send as
 * `clickHash`/`trafficHash`. Both are required downstream — a click without its
 * traffic hash would be attributed as organic — so it is both or neither.
 *
 * `loginCode` (AC-903) is the one-time code that signs a signed-in player into
 * the app on its first launch.
 *
 * Read at click time, never during render: localStorage is browser-only.
 */
export function buildApkDownloadUrl(loginCode?: string): string | null {
    if (!APK_DOWNLOAD_URL) return null;
    const url = new URL(APK_DOWNLOAD_URL);
    const click = safeLocalStorage.getItem("ref");
    const traffic = safeLocalStorage.getItem("traffic");
    if (click && traffic) {
        url.searchParams.set("click", click);
        url.searchParams.set("traffic", traffic);
    }
    if (loginCode) {
        url.searchParams.set("login", loginCode);
    }
    return url.toString();
}

/**
 * Starts the download with a top-level navigation: a `fetch` would pull 100+ MB
 * into memory instead of handing the file to the browser's download manager.
 */
export function startApkDownload(loginCode?: string): void {
    const url = buildApkDownloadUrl(loginCode);
    if (url) window.location.assign(url);
}
